/**
 * Encoding scheme for 32-bit schedulePolicy value
 * 
 * Bit layout:
 * Bits 24-31: Presence ON settings
 *   - Bit 24: Setpoint (1 = normal, 0 = setback)
 *   - Bit 25: Motion sensor action (1 = ON, 0 = OFF)
 *   - Bit 26: Long no action (1 = ON, 0 = OFF)
 *   - Bit 27: Hard operation mode (1 = hard, 0 = soft)
 *   - Bit 28: Occupancy-triggered activation (1 = ON, 0 = OFF)
 * 
 * Bits 16-23: Presence OFF settings
 *   - Bit 16: Setpoint (1 = normal, 0 = setback)
 *   - Bit 17: Motion sensor action (1 = ON, 0 = OFF)
 *   - Bit 18: Long no action (1 = ON, 0 = OFF)
 *   - Bit 19: Hard operation mode (1 = hard, 0 = soft)
 *   - Bit 20: Occupancy-triggered activation (1 = ON, 0 = OFF)
 * 
 * Bits 0-15: General settings
 *   - Bit 0: Soft control switch on
 *   - Bit 1: Soft control switch off
 *   - Bit 2: Enforce minimal setpoint
 *   - Bit 3: Enforce local dashboard offline
 */

export interface PolicyState {
  // Presence ON (setpoint mode)
  setpointOnMode: number; // 0 = setback, 1 = normal
  motionSensorOn: boolean;
  longNoMotionOn: boolean;
  hardModeOn: boolean;
  occupancyTriggeredActivationOn: boolean;

  // Presence OFF (setpoint mode)
  setpointOffMode: number; // 0 = setback, 1 = normal
  motionSensorOff: boolean;
  longNoMotionOff: boolean;
  hardModeOff: boolean;
  occupancyTriggeredActivationOff: boolean;

  // General settings
  softOperationOn: boolean;
  softOperationOff: boolean;
  enforceMinimalSetpoint: boolean;
  enforceLocalDashboardOffline: boolean;
}

/**
 * Encodes individual policy settings into a 32-bit schedulePolicy value
 */
export function encodePolicyToSchedule(state: PolicyState): number {
  let policy = 0;

  // Presence ON settings (bits 24-28)
  if (state.setpointOnMode === 1) policy |= 1 << 24; // normal cooling
  if (state.motionSensorOn) policy |= 1 << 25;
  if (state.longNoMotionOn) policy |= 1 << 26;
  if (state.hardModeOn) policy |= 1 << 27;
  if (state.occupancyTriggeredActivationOn) policy |= 1 << 28;

  // Presence OFF settings (bits 16-20)
  if (state.setpointOffMode === 1) policy |= 1 << 16; // normal cooling
  if (state.motionSensorOff) policy |= 1 << 17;
  if (state.longNoMotionOff) policy |= 1 << 18;
  if (state.hardModeOff) policy |= 1 << 19;
  if (state.occupancyTriggeredActivationOff) policy |= 1 << 20;

  // General settings (bits 0-3)
  if (state.softOperationOn) policy |= 1 << 0;
  if (state.softOperationOff) policy |= 1 << 1;
  if (state.enforceMinimalSetpoint) policy |= 1 << 2;
  if (state.enforceLocalDashboardOffline) policy |= 1 << 3;

  return policy;
}

/**
 * Decodes a 32-bit schedulePolicy value into individual policy settings
 */
export function decodePolicyFromSchedule(policy: number): PolicyState {
  return {
    // Presence ON
    setpointOnMode: (policy & (1 << 24)) ? 1 : 0,
    motionSensorOn: Boolean(policy & (1 << 25)),
    longNoMotionOn: Boolean(policy & (1 << 26)),
    hardModeOn: Boolean(policy & (1 << 27)),
    occupancyTriggeredActivationOn: Boolean(policy & (1 << 28)),

    // Presence OFF
    setpointOffMode: (policy & (1 << 16)) ? 1 : 0,
    motionSensorOff: Boolean(policy & (1 << 17)),
    longNoMotionOff: Boolean(policy & (1 << 18)),
    hardModeOff: Boolean(policy & (1 << 19)),
    occupancyTriggeredActivationOff: Boolean(policy & (1 << 20)),

    // General settings
    softOperationOn: Boolean(policy & (1 << 0)),
    softOperationOff: Boolean(policy & (1 << 1)),
    enforceMinimalSetpoint: Boolean(policy & (1 << 2)),
    enforceLocalDashboardOffline: Boolean(policy & (1 << 3))
  };
}

/**
 * Generates the default schedulePolicy value
 * Defaults:
 * - Presence ON: normal setpoint, motion sensor active
 * - Presence OFF: setback setpoint, motion sensor active
 * - General: enforce minimal setpoint, soft control switch off
 */
export function getDefaultSchedulePolicy(): number {
  return encodePolicyToSchedule({
    // Presence ON defaults
    setpointOnMode: 1, // normal (true)
    motionSensorOn: true,
    longNoMotionOn: false,
    hardModeOn: false,
    occupancyTriggeredActivationOn: false,

    // Presence OFF defaults
    setpointOffMode: 0, // setback (false)
    motionSensorOff: true,
    longNoMotionOff: false,
    hardModeOff: false,
    occupancyTriggeredActivationOff: false,

    // General defaults
    softOperationOn: false,
    softOperationOff: true,
    enforceMinimalSetpoint: true,
    enforceLocalDashboardOffline: false
  });
}
